import React, { useEffect, useState, useCallback } from 'react';
import { IRolesEditProps } from './RolesForUserEdit.types';
import { IRoleForUser } from '../../../services/rolesAndPermissions/rolesAndPermissions.types';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { useLayoutContext } from '../../../Context/Layout.context';
import { Grid } from '@material-ui/core';
import { VerticalTabs } from '../../Tabs/VerticalTabs/VerticalTabs.component';
import { TabItem } from '../../Tabs/VerticalTabs/VerticalTabs.types';
import { useStyles } from './RolesForUserEdit.styles';
import { RolesForUserList } from './RolesForUserList.component';
import { RoleActivity } from './RolesForUserList.types';
import { getActivityLabel } from './helpers';
import _ from 'lodash';
import Typography from '@material-ui/core/Typography';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { setRolesForUser } from '../../../services/rolesAndPermissions/rolesAndPermissions.service';
import useDocumentTitle from '../../../hooks/DocumentTitle/documentTitle';

enum TabKey {
  active,
  inActive,
  all,
  search,
}

export const RolesForUserEdit: React.FC<IRolesEditProps> = ({ userId, userName, roles, onClose }) => {
  const classes = useStyles();
  const [userRoles, setUserRoles] = useState<IRoleForUser[]>(_.cloneDeep(roles));
  const [activeRoles, setActiveRoles] = useState<IRoleForUser[]>([]);
  const [changedToActive, setChangedToActive] = useState<string[]>([]);
  const [changedToInActive, setChangedToInActive] = useState<string[]>([]);
  const [tabItems, setTabItems] = useState<TabItem[]>([]);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [currentActivity, setCurrentActivity] = useState<RoleActivity | undefined>(RoleActivity.active);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [defaultRoleNotAssignToUser, setDefaultRoleNotAssignToUser] = useState<IRoleForUser[]>([]);
  const { showLoader, openAlert } = useLayoutContext();
  useDocumentTitle('Zarządzanie Rolami');

  const toggleRoleActivity = (roleId: string) => {
    const newRoles: IRoleForUser[] = _.cloneDeep(userRoles).map((role: IRoleForUser) => {
      if (role.id === roleId) {
        if (role.isUserAssignedToRole) {
          setChangedToInActive([...changedToInActive, roleId]);
          setChangedToActive(_.without(changedToActive, roleId));
        } else {
          setChangedToActive([...changedToActive, roleId]);
          setChangedToInActive(_.without(changedToInActive, roleId));
        }
        role.isUserAssignedToRole = !role.isUserAssignedToRole;
      }
      return role;
    });
    setUserRoles(newRoles);
  };

  useEffect(() => {
    setIsDataChanged(JSON.stringify(userRoles) !== JSON.stringify(roles));
  }, [userRoles]);

  const clearData = () => {
    setUserRoles(_.cloneDeep(roles));
    setIsDataChanged(false);
    setCurrentActivity(RoleActivity.active);
  };

  const handleCancel = () => {
    if (!isDataChanged) {
      onClose && onClose();
      return;
    }
    clearData();
  };

  const handleSave = () => {
    showLoader(true, 'Aktualizuję dane');
    setRolesForUser(userId, changedToActive, changedToInActive)
      .then(
        () => {
          openAlert('success');
          onClose && onClose();
        },
        () => {
          openAlert('save_error');
          clearData();
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const getRolesLenght = (activity: RoleActivity): number => {
    const userRolesLength = userRoles.length - defaultRoleNotAssignToUser.length;
    switch (activity) {
      case RoleActivity.active:
        return activeRoles.length;
      case RoleActivity.inActive:
        return userRolesLength - activeRoles.length;
      case RoleActivity.all:
        return userRolesLength;
      default:
        return 0;
    }
  };

  const getTabLabel = (activity: RoleActivity) => {
    const rolesListLength = getRolesLenght(activity);
    return `${getActivityLabel(activity)} (${rolesListLength})`;
  };

  const getActiveTabLabel = useCallback(() => getTabLabel(RoleActivity.active), [activeRoles]);

  const getInActiveTabLabel = useCallback(() => getTabLabel(RoleActivity.inActive), [activeRoles, userRoles]);

  const getAllTabLabel = useCallback(() => getTabLabel(RoleActivity.all), [userRoles, activeRoles]);

  useEffect(() => {
    const newActiveRoles: IRoleForUser[] = userRoles.filter((item: IRoleForUser) => item.isUserAssignedToRole);
    const isDefault: IRoleForUser[] = userRoles.filter(
      (item: IRoleForUser) => item.isDefaultRole && !item.isUserAssignedToRole,
    );
    setActiveRoles(newActiveRoles);
    setDefaultRoleNotAssignToUser(isDefault);
  }, [userRoles]);

  const getActivityFromTab = (tabKey: TabKey): RoleActivity | undefined => {
    switch (tabKey) {
      case TabKey.active:
        return RoleActivity.active;
      case TabKey.inActive:
        return RoleActivity.inActive;
      case TabKey.all:
        return RoleActivity.all;
      default:
        return undefined;
    }
  };

  const getTabFromActivity = (activity: RoleActivity | undefined): TabKey => {
    switch (activity) {
      case RoleActivity.active:
        return TabKey.active;
      case RoleActivity.inActive:
        return TabKey.inActive;
      case RoleActivity.all:
        return TabKey.all;
      default:
        return TabKey.search;
    }
  };

  const handleChangeTab = (tabId: number) => {
    setCurrentActivity(getActivityFromTab(tabId as TabKey));
  };

  useEffect(() => {
    const items = [
      {
        label: 'active',
        getInteractiveLabel: getActiveTabLabel,
        children: <RolesForUserList userId={userId} roles={activeRoles} onToggleRolesActivity={toggleRoleActivity} />,
      },
      {
        label: 'inactive',
        getInteractiveLabel: getInActiveTabLabel,
        children: (
          <RolesForUserList
            userId={userId}
            roles={_.difference(userRoles, activeRoles)}
            onToggleRolesActivity={toggleRoleActivity}
          />
        ),
      },
      {
        label: 'all',
        getInteractiveLabel: getAllTabLabel,
        children: <RolesForUserList userId={userId} roles={userRoles} onToggleRolesActivity={toggleRoleActivity} />,
      },
    ];
    setTabItems(items);
  }, [activeRoles, userRoles]);

  return (
    <>
      <ModalWithButtons
        xCloseButton={() => (!isDataChanged ? onClose && onClose() : setIsLeavePromptOpen(true))}
        id={userId}
        open={true}
        size="md"
        title={<>Zarządzanie Rolami: {userName}</>}
        onCancel={handleCancel}
        onConfirm={handleSave}
        confirmButtonDisabled={!isDataChanged}
        buttonsContainerStyle={{ margin: '10px 0 0 0' }}
      >
        <Grid container>
          <VerticalTabs
            items={tabItems}
            style={{ height: '400px', width: '200px' }}
            selectedTab={getTabFromActivity(currentActivity)}
            onChange={handleChangeTab}
            tabPanelHeader={
              <div className={classes.listHeader}>
                <Typography className={classes.tabTitle}>
                  {currentActivity !== undefined ? <>{getTabLabel(currentActivity)}</> : <></>}
                </Typography>
              </div>
            }
          />
        </Grid>
      </ModalWithButtons>
      {isLeavePromptOpen && (
        <LeavePrompt isOpen={true} onConfirm={onClose} onCancel={() => setIsLeavePromptOpen(false)} />
      )}
    </>
  );
};
